import {Dispatch} from "redux";

import {IFetchContext} from "@pg-mono/data-fetcher";
import {isEmpty, isNumber, pick} from "@pg-mono/nodash";
import {catch400, catch404, getRequest, postRequest} from "@pg-mono/request";
import {createRequestActionTypes} from "@pg-mono/request-state";
import {apiLink, apiPath} from "@pg-mono/rp-api-routes";

import {IRPStore} from "../../../app/rp_reducer";
import {IRPRequestMeta} from "../../../app/rp_request_meta";
import {redirectOrEnable404ResponseState} from "../../../errors/actions/page_404_actions";
import {Country} from "../../../region/types/Country";
import {fetchSelectedRegionById} from "../../list/actions/offer_list_selected_data_actions";
import {OfferListSubType} from "../../list/types/OfferListSubType";
import {ISearchAPIResponse} from "../../types/ISearchAPIResponse";
import {mapSearchToSearchQuery} from "../../utils/map_search_to_search_query";
import {fromParsedObjectToQuery, ISlugParsed, ISlugQuery} from "../friendly_offer_list/offer_url_parser";
import {getGeneratedName} from "./generate_name";
import {prepareSaveSearchValues} from "./prepare_save_search_values";

const FETCH_OFFER_LIST_SEO_CONTENT = "offer_list/fetch_search";
export const fetchOfferListSearchActionTypes = createRequestActionTypes(FETCH_OFFER_LIST_SEO_CONTENT);
const isBrowser = process.env.EXEC_ENV === "browser";

export const fetchSearchParsedOnError =
    (ctx: IFetchContext<IRPRequestMeta>) =>
    (dispatch: Dispatch): Promise<Partial<ISlugQuery> | void> => {
        dispatch({type: fetchOfferListSearchActionTypes.start});

        const url = apiLink.search.search({})({friendlySlug: ctx.match.params.friendlySlug});
        return getRequest(ctx.meta, url)
            .then(async (search: ISearchAPIResponse) => {
                dispatch({type: fetchOfferListSearchActionTypes.success, result: search});

                if (isNumber(search.region)) {
                    dispatch(fixSearchRegion(search, ctx.meta));
                }

                const searchParsed = mapSearchToSearchQuery(search, ctx.prevResult.price_lower_than_average);

                return fromParsedObjectToQuery(searchParsed);
            })
            .catch(
                catch404(async (error: unknown) => {
                    dispatch({type: fetchOfferListSearchActionTypes.error, error});
                    // when we cannot parse slug and there is no search we need to trigger 404
                    await dispatch(redirectOrEnable404ResponseState(ctx.route.pathname, ctx.meta));
                })
            );
    };

export const fetchSearchAtRoute =
    (
        ctx: IFetchContext<
            IRPRequestMeta,
            {friendlySlug: string; offerListSubType?: OfferListSubType; offerListSubFilter?: string; country?: string},
            ISlugParsed
        >
    ) =>
    async (dispatch: Dispatch, getState: () => IRPStore) => {
        dispatch({type: fetchOfferListSearchActionTypes.start});

        // prevent search call on Abroad without region
        if (ctx.prevResult.country != Country.POLAND && !ctx.prevResult.region_name) {
            dispatch({type: fetchOfferListSearchActionTypes.success, result: null});
        }

        async function onSearchSuccess(search: ISearchAPIResponse): Promise<Partial<ISlugQuery>> {
            if (isNumber(search.region)) {
                dispatch(fixSearchRegion(search, ctx.meta));
            }

            const searchParsed: Partial<ISlugParsed> = mapSearchToSearchQuery(search, ctx.prevResult.price_lower_than_average);
            const searchQuery: Partial<ISlugQuery> = fromParsedObjectToQuery(searchParsed);
            return {
                // we need to add house filters & floor choices manually because API doesn't handle it properly
                ...pick(ctx.prevResult, ["house_additional_areas", "house_type", "house_storeys"]),
                ...(pick(ctx.prevResult, ["floor_choices"]) as unknown as {floor_choices: string[] | string | undefined}),
                ...pick(ctx.prevResult, ["country", "region"]),
                ...pick(ctx.prevResult, ["region_name", "region_name", "distance"]),

                ...ctx.route.query,
                ...searchQuery
            };
        }
        const search: ISearchAPIResponse | null = getState().offerList.search;

        if (!isBrowser && search) {
            return onSearchSuccess(search);
        }

        const friendlySlug = `${ctx.match.params.friendlySlug}${ctx.prevResult.country && ctx.prevResult.country != Country.POLAND ? `-${ctx.prevResult.region_name}` : ""}`;
        const url = apiLink.search.search({})({
            friendlySlug
        });

        return getRequest(ctx.meta, url)
            .then(async (search: ISearchAPIResponse) => {
                dispatch({type: fetchOfferListSearchActionTypes.success, result: search});
                return onSearchSuccess(search);
            })
            .catch(
                catch404(async (error: unknown) => {
                    dispatch({type: fetchOfferListSearchActionTypes.error, error});
                    // when we have parsed slug successfully and there is no search
                    if (isBrowser) {
                        // in browser we add search entry
                        const {selectedRegions} = getState().offerList;

                        const saveSearchResponse = await dispatch(
                            saveSearch(
                                {
                                    slug: ctx.match.params.friendlySlug,
                                    type: ctx.prevResult.type || null,
                                    region: !isEmpty(ctx.prevResult.region) ? parseInt(ctx.prevResult.region[0], 10) : null,
                                    ...prepareSaveSearchValues(ctx.prevResult),
                                    name: getGeneratedName(ctx.prevResult, selectedRegions.length ? selectedRegions[0].short_name : undefined)(ctx)
                                },
                                ctx.meta
                            )
                        );

                        return {
                            ...ctx.prevResult,
                            saveSearchResponse
                        };
                    } else {
                        return ctx.prevResult;
                    }
                })
            );
    };

export const saveSearch = (searchData: Partial<ISearchAPIResponse>, meta: IRPRequestMeta) => (): Promise<ISearchAPIResponse | void> => {
    return postRequest(meta, apiPath.search.search.base, searchData)
        .then((search: ISearchAPIResponse) => {
            return search;
        })
        .catch(
            catch400(() => {
                // search does not have to successfully save the slug and related query params
            })
        );
};

//  Utils
function fixSearchRegion(search: ISearchAPIResponse, meta: IRPRequestMeta) {
    return async (dispatch: Dispatch) => {
        if (isNumber(search.region)) {
            // we should update selected data because search may give us different region
            await dispatch(fetchSelectedRegionById(meta, search.region));
        }
    };
}
import {Dispatch} from "redux";

import {IFetchContext} from "@pg-mono/data-fetcher";
import {isEmpty, isNumber, pick} from "@pg-mono/nodash";
import {catch400, catch404, getRequest, postRequest} from "@pg-mono/request";
import {createRequestActionTypes} from "@pg-mono/request-state";
import {apiLink, apiPath} from "@pg-mono/rp-api-routes";

import {IRPStore} from "../../../app/rp_reducer";
import {IRPRequestMeta} from "../../../app/rp_request_meta";
import {redirectOrEnable404ResponseState} from "../../../errors/actions/page_404_actions";
import {Country} from "../../../region/types/Country";
import {fetchSelectedRegionById} from "../../list/actions/offer_list_selected_data_actions";
import {OfferListSubType} from "../../list/types/OfferListSubType";
import {ISearchAPIResponse} from "../../types/ISearchAPIResponse";
import {mapSearchToSearchQuery} from "../../utils/map_search_to_search_query";
import {fromParsedObjectToQuery, ISlugParsed, ISlugQuery} from "../friendly_offer_list/offer_url_parser";
import {getGeneratedName} from "./generate_name";
import {prepareSaveSearchValues} from "./prepare_save_search_values";

const FETCH_OFFER_LIST_SEO_CONTENT = "offer_list/fetch_search";
export const fetchOfferListSearchActionTypes = createRequestActionTypes(FETCH_OFFER_LIST_SEO_CONTENT);
const isBrowser = process.env.EXEC_ENV === "browser";

export const fetchSearchParsedOnError =
    (ctx: IFetchContext<IRPRequestMeta>) =>
    (dispatch: Dispatch): Promise<Partial<ISlugQuery> | void> => {
        dispatch({type: fetchOfferListSearchActionTypes.start});

        const url = apiLink.search.search({})({friendlySlug: ctx.match.params.friendlySlug});
        return getRequest(ctx.meta, url)
            .then(async (search: ISearchAPIResponse) => {
                dispatch({type: fetchOfferListSearchActionTypes.success, result: search});

                if (isNumber(search.region)) {
                    dispatch(fixSearchRegion(search, ctx.meta));
                }

                const searchParsed = mapSearchToSearchQuery(search, ctx.prevResult.price_lower_than_average);

                return fromParsedObjectToQuery(searchParsed);
            })
            .catch(
                catch404(async (error: unknown) => {
                    dispatch({type: fetchOfferListSearchActionTypes.error, error});
                    // when we cannot parse slug and there is no search we need to trigger 404
                    await dispatch(redirectOrEnable404ResponseState(ctx.route.pathname, ctx.meta));
                })
            );
    };

export const fetchSearchAtRoute =
    (
        ctx: IFetchContext<
            IRPRequestMeta,
            {friendlySlug: string; offerListSubType?: OfferListSubType; offerListSubFilter?: string; country?: string},
            ISlugParsed
        >
    ) =>
    async (dispatch: Dispatch, getState: () => IRPStore) => {
        dispatch({type: fetchOfferListSearchActionTypes.start});

        // prevent search call on Abroad without region
        if (ctx.prevResult.country != Country.POLAND && !ctx.prevResult.region_name) {
            dispatch({type: fetchOfferListSearchActionTypes.success, result: null});
        }

        async function onSearchSuccess(search: ISearchAPIResponse): Promise<Partial<ISlugQuery>> {
            if (isNumber(search.region)) {
                dispatch(fixSearchRegion(search, ctx.meta));
            }

            const searchParsed: Partial<ISlugParsed> = mapSearchToSearchQuery(search, ctx.prevResult.price_lower_than_average);
            const searchQuery: Partial<ISlugQuery> = fromParsedObjectToQuery(searchParsed);
            return {
                // we need to add house filters & floor choices manually because API doesn't handle it properly
                ...pick(ctx.prevResult, ["house_additional_areas", "house_type", "house_storeys"]),
                ...(pick(ctx.prevResult, ["floor_choices"]) as unknown as {floor_choices: string[] | string | undefined}),
                ...pick(ctx.prevResult, ["country", "region"]),
                ...pick(ctx.prevResult, ["region_name", "region_name", "distance"]),

                ...ctx.route.query,
                ...searchQuery
            };
        }
        const search: ISearchAPIResponse | null = getState().offerList.search;

        if (!isBrowser && search) {
            return onSearchSuccess(search);
        }

        const friendlySlug = `${ctx.match.params.friendlySlug}${ctx.prevResult.country && ctx.prevResult.country != Country.POLAND ? `-${ctx.prevResult.region_name}` : ""}`;
        const url = apiLink.search.search({})({
            friendlySlug
        });

        return getRequest(ctx.meta, url)
            .then(async (search: ISearchAPIResponse) => {
                dispatch({type: fetchOfferListSearchActionTypes.success, result: search});
                return onSearchSuccess(search);
            })
            .catch(
                catch404(async (error: unknown) => {
                    dispatch({type: fetchOfferListSearchActionTypes.error, error});
                    // when we have parsed slug successfully and there is no search
                    if (isBrowser) {
                        // in browser we add search entry
                        const {selectedRegions} = getState().offerList;

                        const saveSearchResponse = await dispatch(
                            saveSearch(
                                {
                                    slug: ctx.match.params.friendlySlug,
                                    type: ctx.prevResult.type || null,
                                    region: !isEmpty(ctx.prevResult.region) ? parseInt(ctx.prevResult.region[0], 10) : null,
                                    ...prepareSaveSearchValues(ctx.prevResult),
                                    name: getGeneratedName(ctx.prevResult, selectedRegions.length ? selectedRegions[0].short_name : undefined)(ctx)
                                },
                                ctx.meta
                            )
                        );

                        return {
                            ...ctx.prevResult,
                            saveSearchResponse
                        };
                    } else {
                        return ctx.prevResult;
                    }
                })
            );
    };

export const saveSearch = (searchData: Partial<ISearchAPIResponse>, meta: IRPRequestMeta) => (): Promise<ISearchAPIResponse | void> => {
    return postRequest(meta, apiPath.search.search.base, searchData)
        .then((search: ISearchAPIResponse) => {
            return search;
        })
        .catch(
            catch400(() => {
                // search does not have to successfully save the slug and related query params
            })
        );
};

//  Utils
function fixSearchRegion(search: ISearchAPIResponse, meta: IRPRequestMeta) {
    return async (dispatch: Dispatch) => {
        if (isNumber(search.region)) {
            // we should update selected data because search may give us different region
            await dispatch(fetchSelectedRegionById(meta, search.region));
        }
    };
}
